// Cliente de feature flags compartilhado entre portais (student/teacher/
// company/manager/auth/pay). Cada portal chama `bootstrapFeatureFlags()` no
// startup (logo após o login) e depois usa `flagEnabled(key)` em qualquer
// lugar pra esconder/mostrar UI.
//
// Backend: GET /api/user/feature-flags?platform=<portal> devolve apenas as
// flags da plataforma + 'any'. Sem auth: vazio.
//
// Comportamento conservador: se a flag NÃO existir no DB, `flagEnabled` volta
// `defaultValue` (default false). Não quebra a UI quando o admin ainda não
// criou a flag — a feature fica escondida até alguém habilitar.

import { signal } from "@preact/signals";

export type FlagPlatform = "platform" | "company" | "teacher" | "student" | "any";

interface RemoteFlag {
  key: string;
  enabled: boolean;
  label: string;
  platform: string;
}

interface FlagsState {
  loaded: boolean;
  /** Map de key → enabled. Flags ausentes contam como `defaultValue` no flagEnabled. */
  byKey: Record<string, boolean>;
}

export const featureFlagsState = signal<FlagsState>({ loaded: false, byKey: {} });

/** Chame uma vez no boot do portal, depois do api.me() resolver. Se falhar,
 *  flags ficam vazias — `flagEnabled` usa default. */
export async function bootstrapFeatureFlags(platform: FlagPlatform): Promise<void> {
  try {
    const r = await fetch(
      `/api/user/feature-flags?platform=${encodeURIComponent(platform)}`,
      { credentials: "include" }
    );
    if (!r.ok) {
      featureFlagsState.value = { loaded: true, byKey: {} };
      return;
    }
    const data = (await r.json()) as { flags?: RemoteFlag[] };
    const byKey: Record<string, boolean> = {};
    for (const f of data.flags ?? []) byKey[f.key] = f.enabled;
    featureFlagsState.value = { loaded: true, byKey };
  } catch {
    featureFlagsState.value = { loaded: true, byKey: {} };
  }
}

/** Retorna se a flag está habilitada pro usuário corrente. `defaultValue`
 *  governa o caso "flag não existe no DB" (default false — conservador). */
export function flagEnabled(key: string, defaultValue = false): boolean {
  const s = featureFlagsState.value;
  if (!s.loaded) return defaultValue;
  if (key in s.byKey) return s.byKey[key];
  return defaultValue;
}

/** Pra debug — lista flags conhecidas e o estado atual. */
export function dumpFlags(): { loaded: boolean; flags: Record<string, boolean> } {
  return { loaded: featureFlagsState.value.loaded, flags: featureFlagsState.value.byKey };
}
